import { useEffect, useState } from 'react';
import { App } from 'antd';
import { useNavigate } from 'react-router';
import WorkSpaceService from 'services/master/workspace';

const services = new WorkSpaceService();

const useComponent = ({ w_id }) => {
  const { message, modal } = App.useApp();
  const navigate = useNavigate();
  const [workspace, setWorkspace] = useState(null);
  const [history, setHistory] = useState([]);
  const [demoCredentials, setDemoCredentials] = useState(null);
  const [loading, setLoading] = useState(false);
  const [sampleModalOpen, setSampleModalOpen] = useState(false);
  const [sampleSubmitting, setSampleSubmitting] = useState(false);
  const [sampleDeleting, setSampleDeleting] = useState(false);
  const [aiChatSaving, setAiChatSaving] = useState(false);

  const load = async () => {
    if (!w_id) return;
    setLoading(true);
    try {
      const result = await services.detail({ w_id });
      setWorkspace(result?.workspace || null);
      setHistory(Array.isArray(result?.history) ? result.history : []);
      setDemoCredentials(result?.demo_credentials || null);
    } catch (e) {
      message.error(e?.message || 'Lấy thông tin thất bại');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, [w_id]);

  const isInvalidateShop = (record) => {
    const tier = record?.subscription?.tier || 'mien_phi';
    const expiryOn = record?.subscription?.expiry_on;
    if (tier !== 'mien_phi' || !expiryOn) return false;
    const expiryTime = new Date(expiryOn).getTime();
    if (Number.isNaN(expiryTime)) return false;
    return expiryTime < Date.now();
  };

  const packageStatusLabel = (status) => {
    const map = {
      payment_pending: 'Chờ thanh toán',
      payment_confirmed: 'Chờ duyệt',
      approved: 'Đã duyệt',
    };
    return map[status] || status;
  };

  const packageStatusColor = (status) => {
    const map = {
      payment_pending: 'default',
      payment_confirmed: 'orange',
      approved: 'green',
    };
    return map[status] || 'default';
  };

  const goBack = () => navigate('/workspace');

  const openSampleModal = () => setSampleModalOpen(true);
  const closeSampleModal = () => {
    if (!sampleSubmitting) {
      setSampleModalOpen(false);
    }
  };

  const copyText = async (text, label = 'Nội dung') => {
    try {
      await navigator.clipboard.writeText(String(text || ''));
      message.success(`Đã copy ${label}`);
    } catch {
      message.error('Copy thất bại');
    }
  };

  const copyAllDemoCredentials = async () => {
    if (!demoCredentials && !workspace) return;
    const lines = [
      `Shop: ${workspace?.web_site?.title || ''}`,
      `URL: https://${workspace?.web_site?.url || ''}.sipro.store`,
      `Login ID: ${demoCredentials?.login_id || ''}`,
      `Mật khẩu: ${demoCredentials?.password || ''}`,
    ];
    await copyText(lines.join('\n'), 'thông tin đăng nhập');
  };

  const createSampleData = async ({ shop_description_prompt, reset_homepage }) => {
    if (!w_id) return;
    setSampleSubmitting(true);
    try {
      const result = await services.createSampleData({
        w_id,
        shop_description_prompt,
        reset_homepage,
      });
      message.success(
        `Đã tạo dữ liệu mẫu: ${result?.products_created || 0} sản phẩm, ${result?.tags_created || 0} tag, ${result?.categories_created || 0} category, ${result?.collections_created || 0} collection`
      );
      setSampleModalOpen(false);
      await load();
    } catch (e) {
      message.error(e?.message || 'Tạo dữ liệu mẫu thất bại');
    } finally {
      setSampleSubmitting(false);
    }
  };

  const deleteSampleData = () => {
    if (!w_id || sampleDeleting) return;

    modal.confirm({
      title: 'Xóa dữ liệu mẫu',
      content: 'Sẽ xóa toàn bộ dữ liệu mẫu đã sinh (sản phẩm, danh mục, tag, bộ sưu tập) và chuyển trang chủ về chế độ mặc định. Bạn có chắc chắn?',
      okText: 'Xóa dữ liệu mẫu',
      okButtonProps: { danger: true, loading: sampleDeleting },
      cancelText: 'Hủy',
      onOk: async () => {
        setSampleDeleting(true);
        try {
          const result = await services.deleteSampleData({ w_id });
          message.success(
            `Đã xóa dữ liệu mẫu: ${result?.deleted_products || 0} sản phẩm, ${result?.deleted_categories || 0} category, ${result?.deleted_tags || 0} tag, ${result?.deleted_collections || 0} collection`
          );
          await load();
        } catch (e) {
          message.error(e?.message || 'Xóa dữ liệu mẫu thất bại');
        } finally {
          setSampleDeleting(false);
        }
      },
    });
  };

  const aiChatEnabled = workspace?.ai_chat_enabled ?? false;

  const onToggleAiChat = async (enabled) => {
    setAiChatSaving(true);
    try {
      await services.toggleAiChat({ w_id, enabled });
      setWorkspace((prev) => ({ ...(prev || {}), ai_chat_enabled: enabled }));
      message.success(enabled ? 'Đã bật AI trả lời tự động' : 'Đã tắt AI trả lời tự động');
    } catch (e) {
      message.error(e?.message || 'Cập nhật thất bại');
    } finally {
      setAiChatSaving(false);
    }
  };


  return {
    workspace,
    history,
    demoCredentials,
    loading,
    isInvalidateShop,
    packageStatusLabel,
    packageStatusColor,
    goBack,
    copyText,
    copyAllDemoCredentials,
    sampleModalOpen,
    sampleSubmitting,
    openSampleModal,
    closeSampleModal,
    createSampleData,
    sampleDeleting,
    deleteSampleData,
    aiChatEnabled,
    aiChatSaving,
    onToggleAiChat,
  };
};

export default useComponent;
